import {ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal} from '@angular/core';
import {FormGroup} from "@angular/forms";
import {RecordData, SelectData} from "../../../../../types/records-table.types";
import {MatTab, MatTabChangeEvent, MatTabGroup} from "@angular/material/tabs";
import {BankDetailsModalService} from "../../services/bank-details-modal.service";
import {BankDetailsDialogData, CreateBankDetailsParams} from "../../types/bank-details-modal.types";
import {BankDetailsFormComponent} from "../bank-details-form/bank-details-form.component";
import {MatButton} from "@angular/material/button";
import {BankDetailsFormField} from "../../types/bank-details-form-field.types";
import {bankDetailsFormFields} from "../../configs/bank-details-form-fields";
import {BaseModalService} from "../../../services/base-modal.service";
import {MatExpansionModule} from "@angular/material/expansion";
import {defaultRecordData} from "../../../../../models/default-record-data";
import {AttachProps} from "../../../../../types/attach-detach.types";
import {CloseModalReason} from "../../../../../types/modal.types";
import {dateFields} from "../../configs/bank-details-modal-config";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-new-bank-details',
  standalone: true,
  imports: [
    MatTabGroup,
    MatTab,
    BankDetailsFormComponent,
    MatButton,
    MatExpansionModule,
    LoaderComponent
  ],
  templateUrl: './new-bank-details.component.html',
  styleUrls: [
    './new-bank-details.component.scss',
    '../../bank-details-modal.component.scss'
  ],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewBankDetailsComponent implements OnInit {
  bankDetailsDialogData = input<BankDetailsDialogData>();
  legalEntityTypes = input<Array<SelectData>>([]);
  handleSaveButtonClick = output<number>();
  closeDialog = output<CloseModalReason>();

  private bankDetailsModalService: BankDetailsModalService = inject(BankDetailsModalService);
  private baseModalService: BaseModalService = inject(BaseModalService);

  public selectedTabIndex = signal<number>(0);
  public bankDetailsData = signal<Array<RecordData>>([]);
  public bankDetailsFormGroups = signal<Array<FormGroup>>([]);
  public isBankDetailsFormValid = signal<boolean>(false);
  public recordCalendarId = computed(() => this.baseModalService.getRecordCalendarId(this.bankDetailsDialogData()?.recordData || defaultRecordData));
  public bloggerId = computed(() => this.bankDetailsDialogData()?.bloggerId || 0);

  public async ngOnInit(): Promise<void> {
    await this.fetchBankDetailsData();
  }

  public async handleTabChange(event: MatTabChangeEvent): Promise<void> {
    this.bankDetailsFormGroups().at(0)?.reset();
    this.selectedTabIndex.set(event.index);
  }

  public getCurrentBankDetailsTab(): string {
    const count = this.bankDetailsData().length || '-';
    return `Выбрать из существующих (${count})`;
  }

  public async saveButtonClick(index: number): Promise<void> {
    if (index === - 1 && !this.bankDetailsModalService.isBirthDateValid(this.bankDetailsFormGroups()[0])) { return; }

    const id = index === - 1 ?
      await this.createBankDetails() :
      await this.attachDankDetails(index);

    this.handleSaveButtonClick.emit(id);
  }

  private async fetchBankDetailsData(): Promise<void> {
    const recordsTableData = await this.bankDetailsModalService.fetchBankDetailsData(this.bloggerId());
    this.bankDetailsData.set(recordsTableData.content);
    const emptyFormGroup = this.baseModalService.getFormGroup<BankDetailsFormField>(bankDetailsFormFields);
    this.bankDetailsFormGroups.set([
      emptyFormGroup,
      ...this.bankDetailsModalService.setFormGroups(this.bankDetailsData())
    ]);
    this.isBankDetailsFormValid.set(true);
  }

  private async attachDankDetails(index: number): Promise<number> {
    const bankDetailsId = Number(this.bankDetailsData().at(index)?.id);
    const attachProps: AttachProps = this.bankDetailsModalService.createAttachProps(this.recordCalendarId(), bankDetailsId);
    await this.bankDetailsModalService.attachRecordData(attachProps)
      .catch(() => this.closeDialog.emit('cancel'));

    return bankDetailsId;
  }

  private async createBankDetails(): Promise<number> {
    const convertedBankDetails = this.baseModalService.convertDateFieldsToRussianFormat<RecordData>(this.bankDetailsFormGroups().at(0)?.value, dateFields);
    const createBankDetailsParams: CreateBankDetailsParams = {
      recordData: {
        ...convertedBankDetails,
        bloggerId: this.bloggerId()
      },
      recordCalendarId: this.recordCalendarId()
    };

    const data = await this.bankDetailsModalService.createBankDetails(createBankDetailsParams)
      .catch(() => defaultRecordData);

    return Number(data.id)
  }
}
